import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';

const SolarChart = ({ historicalData, visibleLines, registerColors, timeRange, siteId }) => {
  // Generate a unique key based on site ID, time range, AND data range to force chart re-render and prevent cross-site caching
  const chartKey = useMemo(() => {
    if (!historicalData || historicalData.length === 0) return `${siteId || 'unknown'}-empty-${timeRange || 'none'}`;
    return `${siteId || 'unknown'}-${timeRange}-${historicalData[0].timestamp}-${historicalData[historicalData.length - 1].timestamp}-${historicalData.length}`;
  }, [historicalData, timeRange, siteId]);

  const chartOption = useMemo(() => {
    if (!historicalData || historicalData.length === 0) {
      return {};
    }

    const firstDate = new Date(historicalData[0].timestamp);
    const lastDate = new Date(historicalData[historicalData.length - 1].timestamp);
    console.log(`Chart rendering ${historicalData.length} data points from ${firstDate.toLocaleDateString()} to ${lastDate.toLocaleDateString()} (timeRange: ${timeRange})`);

    // Create series for each visible line and track which axes are needed
    const series = [];
    let hasWatts = false;
    let hasAmperage = false;
    let hasVoltage = false;
    let hasTemperature = false;
    let hasHumidity = false;

    Object.entries(registerColors).forEach(([key, colorConfig]) => {
      if (visibleLines[key]) {
        // Determine which y-axis to use and data type
        let yAxisIndex = 0; // Default: kW (left)
        const isAmperage = key.includes('Amperage');
        const isInv1Power = key === 'Inv 1 Power';
        const isVoltage = key.includes('Voltage');
        const isTemperature = key.includes('Temperature');
        const isHumidity = key.includes('Humidity');

        if (isInv1Power) {
          yAxisIndex = 1; // Watts (right, first)
          hasWatts = true;
        } else if (isAmperage) {
          yAxisIndex = 2; // mA (right, second)
          hasAmperage = true;
        } else if (isVoltage) {
          yAxisIndex = 3; // Volts (right, third)
          hasVoltage = true;
        } else if (isTemperature) {
          yAxisIndex = 4; // °C (right, fourth)
          hasTemperature = true;
        } else if (isHumidity) {
          yAxisIndex = 5; // % (right, fifth)
          hasHumidity = true;
        }

        const data = historicalData.map(point => {
          let value = point[key] || 0;
          // Convert kW to W for Inv 1 Power
          if (isInv1Power) {
            value = value * 1000;
          }
          // Convert A to mA for amperage
          if (isAmperage) {
            value = value * 1000;
          }
          // Temperature, voltage, and humidity are already in correct units from backend
          return [point.timestamp, value];
        });

        series.push({
          name: colorConfig.label,
          type: 'line',
          data: data,
          smooth: false,
          symbol: 'none',
          showSymbol: false,
          yAxisIndex: yAxisIndex,
          lineStyle: {
            color: colorConfig.color,
            width: 1
          },
          areaStyle: {
            color: colorConfig.color + '20'
          }
        });
      }
    });

    return {
      title: {
        text: 'Power Generation Over Time',
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 600,
          color: '#374151'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        formatter: (params) => {
          const date = new Date(params[0].value[0]);
          let result = `${date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}<br/>`;

          params.forEach(param => {
            let unit = 'kW';
            if (param.seriesName === 'Inv 1 Power') {
              unit = 'W';
            } else if (param.seriesName.includes('Amperage')) {
              unit = 'mA';
            } else if (param.seriesName.includes('Voltage')) {
              unit = 'V';
            } else if (param.seriesName.includes('Temperature')) {
              unit = '°F';
            } else if (param.seriesName.includes('Humidity')) {
              unit = '%';
            }
            result += `${param.marker} ${param.seriesName}: ${param.value[1].toFixed(2)} ${unit}<br/>`;
          });

          return result;
        }
      },
      legend: {
        data: series.map(s => s.name),
        top: 30,
        type: 'scroll'
      },
      grid: {
        left: '60px',
        right: `${60 + (hasWatts ? 80 : 0) + (hasAmperage ? 80 : 0) + (hasVoltage ? 80 : 0) + (hasTemperature ? 80 : 0) + (hasHumidity ? 80 : 0)}px`,
        bottom: '100px',
        top: '80px',
        containLabel: false
      },
      xAxis: {
        type: 'time',
        boundaryGap: false
      },
      yAxis: [
        {
          type: 'value',
          name: 'Power (kW)',
          position: 'left',
          axisLine: { show: true },
          axisTick: { show: true },
          axisLabel: {
            formatter: '{value} kW'
          },
          splitLine: { show: true }
        },
        {
          type: 'value',
          name: 'Power (W)',
          position: 'right',
          offset: 0,
          show: hasWatts,
          axisLine: { show: true },
          axisTick: { show: true },
          axisLabel: {
            formatter: '{value} W'
          },
          splitLine: { show: false }
        },
        {
          type: 'value',
          name: 'Current (mA)',
          position: 'right',
          offset: hasWatts ? 80 : 0,
          show: hasAmperage,
          axisLine: { show: true },
          axisTick: { show: true },
          axisLabel: {
            formatter: '{value} mA'
          },
          splitLine: { show: false }
        },
        {
          type: 'value',
          name: 'Voltage (V)',
          position: 'right',
          offset: (hasWatts ? 80 : 0) + (hasAmperage ? 80 : 0),
          show: hasVoltage,
          axisLine: { show: true },
          axisTick: { show: true },
          axisLabel: {
            formatter: '{value} V'
          },
          splitLine: { show: false }
        },
        {
          type: 'value',
          name: 'Temperature (°F)',
          position: 'right',
          offset: (hasWatts ? 80 : 0) + (hasAmperage ? 80 : 0) + (hasVoltage ? 80 : 0),
          show: hasTemperature,
          axisLine: { show: true },
          axisTick: { show: true },
          axisLabel: {
            formatter: '{value} °F'
          },
          splitLine: { show: false }
        },
        {
          type: 'value',
          name: 'Humidity (%)',
          position: 'right',
          offset: (hasWatts ? 80 : 0) + (hasAmperage ? 80 : 0) + (hasVoltage ? 80 : 0) + (hasTemperature ? 80 : 0),
          show: hasHumidity,
          axisLine: { show: true },
          axisTick: { show: true },
          axisLabel: {
            formatter: '{value} %'
          },
          splitLine: { show: false }
        }
      ],
      dataZoom: [
        {
          type: 'slider',
          show: true,
          xAxisIndex: [0],
          start: 0,
          end: 100,
          height: 40,
          bottom: 10,
          showDetail: false,
          minValueSpan: 3600 * 1000, // Minimum 1 hour span
          showDataShadow: 'auto',
          dataBackground: {
            lineStyle: {
              color: '#3b82f6',
              width: 1,
              opacity: 0.8
            },
            areaStyle: {
              color: '#3b82f6',
              opacity: 0.2
            }
          },
          fillerColor: 'rgba(59, 130, 246, 0.15)',
          borderColor: '#ddd',
          handleStyle: {
            color: '#3b82f6',
            borderColor: '#fff',
            borderWidth: 2
          },
          moveHandleSize: 8,
          emphasis: {
            handleStyle: {
              borderColor: '#3b82f6'
            }
          }
        },
        {
          type: 'inside',
          xAxisIndex: [0],
          start: 0,
          end: 100
        }
      ],
      series: series,
      toolbox: {
        feature: {
          dataZoom: {
            yAxisIndex: 'none'
          },
          restore: {},
          saveAsImage: {
            pixelRatio: 2
          }
        },
        right: 20
      }
    };
  }, [historicalData, visibleLines, registerColors]);

  if (!historicalData || historicalData.length === 0) {
    return (
      <div className="h-[500px] flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
        <div className="text-gray-500">No data available</div>
      </div>
    );
  }

  return (
    <ReactECharts
      key={chartKey}
      option={chartOption}
      style={{ height: '500px', width: '100%' }}
      notMerge={true}
      lazyUpdate={false}
      shouldSetOption={() => true}
      opts={{
        renderer: 'canvas'
      }}
    />
  );
};

export default SolarChart;
